import React, {
  useState,
  useRef,
  useEffect,
  useLayoutEffect,
  useCallback,
  useId,
} from "react";
import { createPortal } from "react-dom";
import { MdOutlineSettings } from "react-icons/md";
import { FaBolt } from "react-icons/fa";
import ModelSpeedToggle from "./ModelSpeedToggle";
import { MODEL_SPEED_FASTEST } from "../../Hooks/useProcessAiModelSpeed";

const PANEL_WIDTH = 300;
const PANEL_HEIGHT_EST = 168;
const GAP = 8;

function computePanelPosition(buttonEl, heightEst = PANEL_HEIGHT_EST) {
  if (typeof window === "undefined" || !buttonEl) return null;
  const rect = buttonEl.getBoundingClientRect();
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const width = Math.min(PANEL_WIDTH, vw - 20);

  let left = rect.right - width;
  left = Math.max(10, Math.min(left, vw - width - 10));

  let top = rect.bottom + GAP;
  if (top + heightEst > vh - 10) {
    top = Math.max(10, rect.top - heightEst - GAP);
  }

  return { top, left, width };
}

/**
 * Compact settings (gear) control that opens a popover for ProcessWithAI model speed.
 */
function ModelSpeedSettingsPopover({
  value,
  onChange,
  disabled = false,
  groupLabel,
  groupHint,
  segmentSmarterLabel,
  segmentFastestLabel,
  smarterTitle,
  fastestTitle,
  settingsButtonTitle,
  /** Panel content direction */
  isRTL = false,
  /** When set with `onFindingsOnlyChange`, shows Findings-only option (Radical / R4V flow). */
  findingsOnly = false,
  onFindingsOnlyChange,
  findingsOnlyLabel,
  findingsOnlyHint,
  /** Smaller trigger (e.g. Radical sidebar toolbar). */
  compact = false,
}) {
  const findingsOnlyLabelId = useId();
  const findingsOnlyHintId = useId();
  const [open, setOpen] = useState(false);
  const btnRef = useRef(null);
  const panelRef = useRef(null);
  const [pos, setPos] = useState(null);

  const panelHeightEst =
    typeof onFindingsOnlyChange === "function" ? 260 : PANEL_HEIGHT_EST;

  const updatePos = useCallback(() => {
    if (!open || !btnRef.current) return;
    setPos(computePanelPosition(btnRef.current, panelHeightEst));
  }, [open, panelHeightEst]);

  useLayoutEffect(() => {
    if (!open) {
      setPos(null);
      return;
    }
    updatePos();
  }, [open, updatePos]);

  useEffect(() => {
    if (!open) return;

    const onPointerDown = (e) => {
      const t = e.target;
      if (
        panelRef.current?.contains(t) ||
        btnRef.current?.contains(t)
      ) {
        return;
      }
      setOpen(false);
    };

    const onKeyDown = (e) => {
      if (e.key === "Escape") setOpen(false);
    };

    window.addEventListener("resize", updatePos);
    window.addEventListener("scroll", updatePos, true);
    document.addEventListener("pointerdown", onPointerDown, true);
    document.addEventListener("keydown", onKeyDown);

    return () => {
      window.removeEventListener("resize", updatePos);
      window.removeEventListener("scroll", updatePos, true);
      document.removeEventListener("pointerdown", onPointerDown, true);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open, updatePos]);

  const toggle = () => {
    if (disabled) return;
    setOpen((o) => !o);
  };

  const panel =
    open &&
    pos &&
    typeof document !== "undefined" &&
    createPortal(
      <div
        ref={panelRef}
        role="dialog"
        aria-label={groupLabel}
        className="fixed z-[100000] rounded-xl border border-gray-200 bg-white p-4 shadow-2xl dark:border-gray-600 dark:bg-gray-800"
        style={{
          top: pos.top,
          left: pos.left,
          width: pos.width,
        }}
        dir={isRTL ? "rtl" : "ltr"}
      >
        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
          {groupLabel}
        </h3>
        <p className="mt-1 text-xs leading-snug text-gray-500 dark:text-gray-400">
          {groupHint}
        </p>
        {typeof onFindingsOnlyChange === "function" && (
          <div className="mt-4 border-t border-gray-200 pt-3 dark:border-gray-600">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <div
                  id={findingsOnlyLabelId}
                  className="text-sm font-medium text-gray-900 dark:text-gray-100"
                >
                  {findingsOnlyLabel}
                </div>
                {findingsOnlyHint ? (
                  <p
                    id={findingsOnlyHintId}
                    className="mt-0.5 text-xs leading-snug text-gray-500 dark:text-gray-400"
                  >
                    {findingsOnlyHint}
                  </p>
                ) : null}
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={Boolean(findingsOnly)}
                aria-labelledby={findingsOnlyLabelId}
                aria-describedby={
                  findingsOnlyHint ? findingsOnlyHintId : undefined
                }
                disabled={disabled}
                onClick={() => onFindingsOnlyChange(!findingsOnly)}
                className={`flex h-7 w-[44px] shrink-0 items-center rounded-full p-0.5 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#A4C639] focus-visible:ring-offset-2 focus-visible:ring-offset-white dark:focus-visible:ring-offset-gray-800 ${
                  findingsOnly
                    ? "bg-[#A4C639]"
                    : "bg-gray-300 dark:bg-gray-600"
                } ${
                  disabled
                    ? "cursor-not-allowed opacity-50"
                    : "cursor-pointer"
                }`}
              >
                <span
                  className={`pointer-events-none block h-6 w-6 rounded-full bg-white shadow transition-[margin-inline-start] duration-200 ease-out dark:bg-gray-100 ${
                    findingsOnly ? "ms-auto" : "ms-0"
                  }`}
                  aria-hidden
                />
              </button>
            </div>
          </div>
        )}
        <div className="mt-3">
          <ModelSpeedToggle
            value={value}
            onChange={(v) => {
              onChange?.(v);
              setOpen(false);
            }}
            disabled={disabled}
            hideGroupLabel
            groupLabel={groupLabel}
            groupHint={groupHint}
            segmentSmarterLabel={segmentSmarterLabel}
            segmentFastestLabel={segmentFastestLabel}
            smarterTitle={smarterTitle}
            fastestTitle={fastestTitle}
          />
        </div>
      </div>,
      document.body,
    );

  return (
    <div className="relative inline-flex shrink-0 items-center">
      <button
        ref={btnRef}
        type="button"
        disabled={disabled}
        onClick={toggle}
        aria-haspopup="dialog"
        aria-expanded={open}
        title={settingsButtonTitle}
        className={`relative flex shrink-0 items-center justify-center rounded-full border border-gray-900/10 bg-[#CBEA7B80] text-gray-900 shadow-sm transition-colors hover:bg-[#A4C639] hover:shadow-md dark:border-gray-700 dark:text-black dark:hover:bg-[#CBEA7B] dark:hover:text-black ${
          compact ? "h-7 w-7 sm:h-8 sm:w-8" : "h-10 w-10"
        } ${
          disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer"
        } ${
          open
            ? "ring-2 ring-[#A4C639] ring-offset-2 ring-offset-white dark:ring-offset-gray-800"
            : ""
        }`}
      >
        <MdOutlineSettings
          className={`leading-none text-gray-900 dark:text-black ${
            compact ? "h-3.5 w-3.5 sm:h-4 sm:w-4" : "text-base"
          }`}
          aria-hidden
        />
        {value === MODEL_SPEED_FASTEST && (
          <span
            className="absolute right-0 top-0 z-[1] flex h-[14px] w-[14px] -translate-y-1/2 translate-x-1/2 items-center justify-center rounded-full bg-[#A4C639] text-gray-900 shadow ring-[1.5px] ring-white dark:ring-gray-800"
            aria-hidden
          >
            <FaBolt className="text-[8px] leading-none" aria-hidden />
          </span>
        )}
      </button>
      {panel}
    </div>
  );
}

export default ModelSpeedSettingsPopover;
